import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import { AUTH_TOKEN } from "constants/AuthConstant";
import AuthService from "services/AuthService";
import {
  fetchDashboardKitchenOrders,
  fetchIntegrationOrders,
  fetchKitchenOrders,
  fetchOrders,
  fetchVendorOrders,
} from "./orderSlice";
import ApiService from "services/ApiService";

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: null,
  userId: "",
  oktaUserId: "",
  customerId: "",
  customerName: "",
  role: {},
  permissions: [],
  type: "",
  maintenance: false,
  success: true,
  customerPartyIds: [],
};

export const signIn = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.login(data);
      if (response.status === 200) {
        const token = response.access_token;
        const decodedToken = jwtDecode(response.access_token);
        if (decodedToken.hasOwnProperty("type"))
          localStorage.setItem(AUTH_TOKEN, token);

        return token;
      } else {
        const { identifier } = response.errorType;
        return rejectWithValue(`${response.description} - ${identifier}`);
      }
    } catch (err) {
      console.log("Auth Slice Error:", err);
      return rejectWithValue(
        err.response?.data?.message ||
          "Something went wrong. Please try again later.",
      );
    }
  },
);

export const integrationLogin = createAsyncThunk(
  "auth/integrationLogin",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.integrationLogin(data);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      console.log("Auth Slice Error:", err);
      return rejectWithValue(
        err.response?.data?.message ||
          "Something went wrong. Please try again later.",
      );
    }
  },
);

export const signUp = createAsyncThunk(
  "auth/register",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;
    try {
      const response = await AuthService.register({ email, password });
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

export const signOut = createAsyncThunk(
  "auth/logout",
  async (data, { rejectWithValue }) => {
    try {
      if (data !== undefined) {
        await AuthService.logout(data);
        localStorage.removeItem(AUTH_TOKEN);
      }
    } catch (err) {
      localStorage.removeItem(AUTH_TOKEN);
      rejectWithValue("Logout Error");
    }
    return null;
  },
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.forgotPassword(data);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      console.log("Auth Slice Error:", err);
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

export const trackOrder = createAsyncThunk(
  "auth/trackOrder",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.trackOrder(data);
      if (response.status === 200) {
        let { success, message, data } = response.data;
        if (data["@odata.count"] > 0) {
          return {
            orders: data.value,
            totalRecords: data["@odata.count"],
            success: success,
            message: message,
          };
        } else {
          return {
            orders: data.value,
            totalRecords: data["@odata.count"],
            success: false,
            message: "No records found",
          };
        }
      } else {
        const { message } = response;
        return rejectWithValue({
          message: message,
          success: false,
        });
      }
    } catch (err) {
      console.log("Auth Slice Error:", err);
      return rejectWithValue({
        message:
          err.response?.data?.message ||
          "Something went wrong. Please try again later.",
        success: false,
      });
    }
  },
);

export const fetchGlobalSettings = createAsyncThunk(
  "auth/fetchGlobalSettings",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getGlobalSettings(data);
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      console.log("Auth Slice Error:", err);
      return rejectWithValue(
        err.response?.data?.message ||
          "Something went wrong while fetching global settings.",
      );
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
      state.message = "";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
      state.message = "";
      state.showMessage = false;
    },
    setAuthMessage: (state, action) => {
      state.loading = false;
      state.message = action.payload;
      state.showMessage = true;
    },
    clearMessage: (state, action) => {
      state.message = null;
      state.showMessage = false;
    },
    resetApp: () => {
      return initialState; // used to trigger a global reset
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        const decoded = jwtDecode(action.payload);
        if (decoded.hasOwnProperty("type")) {
          const { role, userId, uid, customerId, customerName, type, customer_party_ids } = decoded;
          let permissionsArray = [];
          role.permissions.forEach((object) => {
            permissionsArray.push(object.name);
          });
          state.redirect = "/";
          state.token = action.payload;
          state.userId = userId;
          state.oktaUserId = uid;
          state.customerId = customerId;
          state.customerName = customerName;
          state.permissions = permissionsArray;
          state.role = role;
          state.type = type;
          state.customerPartyIds = customer_party_ids;
        } else {
          state.message = "Something went wrong. Please try again later.";
          state.showMessage = true;
        }
        state.loading = false;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(integrationLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(integrationLogin.fulfilled, (state, action) => {
        // const decoded = jwtDecode(action.payload);
        // state.redirect = "/";
        // state.token = action.payload;
        // state.userId = userId;
        // state.oktaUserId = uid;
        // state.customerId = customerId;
        // state.customerName = customerName;
        // state.permissions = permissionsArray;
        // state.role = role;
        // state.type = type;
        state.loading = false;
      })
      .addCase(integrationLogin.rejected, (state, action) => {
        const { status, message } = action.payload;
        state.success = false;
        state.showMessage = true;
        state.message = message;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        //state.redirect = "/CustomerPortal";
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        //state.redirect = "/";
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        const { message, success } = action.payload;
        state.loading = false;
        state.success = success;
        state.showMessage = true;
        state.message = message;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        const { success, message, error } = action.payload;
        state.message = error;
        state.success = success;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(trackOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(trackOrder.fulfilled, (state, action) => {
        const { message, success } = action.payload;
        state.loading = false;
        state.success = success;
        state.showMessage = true;
        state.message = message;
      })
      .addCase(trackOrder.rejected, (state, action) => {
        const { message, success } = action.payload;
        state.message = message;
        state.success = success;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(fetchGlobalSettings.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGlobalSettings.fulfilled, (state, action) => {
        const { message, success, data } = action.payload;
        state.loading = false;
        state.success = success;
        state.maintenance = data[0].maintenanceMode;
        state.message = message;
        state.showMessage = false;
      })
      .addCase(fetchGlobalSettings.rejected, (state, action) => {
        const { message, success } = action.payload;
        state.message = message;
        state.success = success;
        state.showMessage = false;
        state.loading = false;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        const { error } = action.payload;
        if (error === "Token has been revoked.") {
          state.loading = false;
          state.token = null;
          state.redirect = "/";
        }
      })
      .addCase(fetchIntegrationOrders.rejected, (state, action) => {
        const { error } = action.payload;
        if (error === "Token has been revoked.") {
          state.loading = false;
          state.token = null;
          state.redirect = "/";
        }
      })
      .addCase(fetchVendorOrders.rejected, (state, action) => {
        const { error } = action.payload;
        if (error === "Token has been revoked.") {
          state.loading = false;
          state.token = null;
          state.redirect = "/";
        }
      })
      .addCase(fetchDashboardKitchenOrders.rejected, (state, action) => {
        const { error } = action.payload;
        if (error === "Token has been revoked.") {
          state.loading = false;
          state.token = null;
          state.redirect = "/";
        }
      })
      .addCase(fetchKitchenOrders.rejected, (state, action) => {
        const { error } = action.payload;
        if (error === "Token has been revoked.") {
          state.loading = false;
          state.token = null;
          state.redirect = "/";
        }
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
  setAuthMessage,
  clearMessage,
  resetApp,
} = authSlice.actions;

export default authSlice.reducer;
